// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.comment-sources.spec.ts
import { ArrowSquareOut, Warning } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text, Tooltip } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Preview } from "../../../components/Preview";
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  knowledgeDatabaseRoute,
} from "../../../routes/settings/routes/knowledge-database/route";
import { formatTimeStamp } from "../../../routes/tickets/components/VideoPlayback";
import { type KnowledgeDataRecordProperty } from "../../../utils/parsers/knowledge-db";
import { useGetKnowledgeDataRecord } from "../../../utils/queries/knowledge-db";
import {
  type CitationTarget,
  type TicketMessageCitation,
  citationLabel,
  fileCitationToTarget,
  isFileCitation,
  isKnowledgeDbCitation,
  isKnowledgeRuleCitation,
  isNavigableFileCitation,
  isTextCitation,
} from "../utils/commentSourceUtils";

function NavigableFileCitationLink({
  citation,
  index,
  onCitationClick,
}: {
  citation: TicketMessageCitation;
  index: number;
  onCitationClick?: (citation: CitationTarget) => void;
}) {
  const { t } = useTranslation("ui");

  if (!isFileCitation(citation)) return null;

  const label =
    citation.videoStart != null
      ? formatTimeStamp(citation.videoStart)
      : citation.cellAnchor != null
        ? citationLabel(citation)
        : t("commentCitationLinks.source");
  const ariaLabel =
    citation.videoStart != null
      ? t("commentCitationLinks.navigateToSourceAt", { label })
      : citation.cellAnchor != null
        ? t("commentCitationLinks.navigateToSourceIn", { label })
        : t("commentCitationLinks.navigateToSource");

  return (
    <button
      key={`file-${index}`}
      type="button"
      data-testid="comment-panel-citation"
      onClick={() => onCitationClick?.(fileCitationToTarget(citation))}
      className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-puntt-accent-11 underline underline-offset-2 hover:bg-transparent hover:text-puntt-accent-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
      aria-label={ariaLabel}
    >
      <Text as="span" size="1" weight="medium">
        {label}
      </Text>
      <ArrowSquareOut size={12} aria-hidden="true" />
    </button>
  );
}

function TextCitationLink({ citation }: { citation: TicketMessageCitation }) {
  const { t } = useTranslation("ui");
  if (!isTextCitation(citation)) return null;
  const label = citationLabel(citation);

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <button
          type="button"
          data-testid="comment-panel-citation-text"
          className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-puntt-accent-11 underline underline-offset-2 hover:bg-transparent hover:text-puntt-accent-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
        >
          <Text as="span" size="1" weight="medium">
            {label}
          </Text>
          <ArrowSquareOut size={12} aria-hidden="true" />
        </button>
      </Dialog.Trigger>
      <Dialog.Content
        data-testid="comment-citation-guideline-dialog"
        className="flex max-h-[min(70vh,420px)] flex-col gap-4 overflow-y-auto"
      >
        <Dialog.Title>{t("commentCitationLinks.guidelineSource")}</Dialog.Title>
        <Dialog.Description className="hidden">
          {t("commentCitationLinks.guidelineExcerpt")}
        </Dialog.Description>
        <Text size="2" className="text-puntt-neutral-gray-11">
          <em>{t("commentCitationLinks.fromCustomGuidelines")}</em>
        </Text>
        <Preview text={citation.text} />
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            {t("commentCitationLinks.close")}
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function KnowledgeDbCitationLinkInner({
  citation,
}: {
  citation: TicketMessageCitation & { knowledgeDataRecordId: string };
}) {
  const { t } = useTranslation("ui");
  const recordId = citation.knowledgeDataRecordId;
  const getKnowledgeDataRecord = useGetKnowledgeDataRecord(recordId);
  const [recordPreview, setRecordPreview] = useState<
    KnowledgeDataRecordProperty[] | undefined
  >();

  useEffect(() => {
    if (!getKnowledgeDataRecord.data) return;
    setRecordPreview(getKnowledgeDataRecord.data.data);
  }, [getKnowledgeDataRecord.data]);

  const trigger = (
    <button
      type="button"
      data-testid="comment-panel-citation-kb"
      className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-puntt-accent-11 underline underline-offset-2 hover:bg-transparent hover:text-puntt-accent-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
    >
      <Text as="span" size="1" weight="medium">
        {t("commentCitationLinks.knowledgeDb")}
      </Text>
      <ArrowSquareOut size={12} aria-hidden="true" />
    </button>
  );

  if (getKnowledgeDataRecord.isError) {
    return (
      <Tooltip content={t("commentCitationLinks.sourceDeletedFromKdb")}>
        <span className="inline-flex items-center gap-1 opacity-50">
          {trigger}
        </span>
      </Tooltip>
    );
  }

  if (getKnowledgeDataRecord.isLoading) {
    return <span className="inline-flex opacity-50">{trigger}</span>;
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger>{trigger}</Dialog.Trigger>
      <Dialog.Content className="flex max-h-[min(70vh,420px)] flex-col gap-4 overflow-y-auto">
        <Dialog.Title>
          {t("commentCitationLinks.knowledgeDatabaseSource")}
        </Dialog.Title>
        <Dialog.Description className="hidden">
          {t("commentCitationLinks.knowledgeDatabaseRecord")}
        </Dialog.Description>
        <Flex justify="end">
          <Button variant="outline" size="2" asChild>
            <Link
              to={knowledgeDatabaseRoute.to}
              search={KNOWLEDGE_DATABASE_DEFAULT_SEARCH}
              target="_blank"
              rel="noreferrer"
              className="puntt-link flex gap-1"
            >
              <Text>{t("commentCitationLinks.viewKnowledgeDatabase")}</Text>
              <ArrowSquareOut width={16} height={16} />
            </Link>
          </Button>
        </Flex>
        <Preview knowledgeDataRecord={recordPreview} />
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            {t("commentCitationLinks.close")}
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function KnowledgeDbCitationLink({
  citation,
}: {
  citation: TicketMessageCitation;
}) {
  if (!isKnowledgeDbCitation(citation)) return null;
  return <KnowledgeDbCitationLinkInner citation={citation} />;
}

/**
 * A citation to a synthesized Knowledge Database rule (`knowledgerules`) — e.g.
 * an approved claim. The chip is self-describing: the citation carries its own
 * `quote`, so unlike the KDB-record chip this needs no follow-up fetch. The
 * dialog shows the cited wording and deep-links into the Rules tab filtered to
 * this exact rule/source so the reviewer can open the governing rule.
 */
function KnowledgeRuleCitationLink({
  citation,
}: {
  citation: TicketMessageCitation;
}) {
  const { t } = useTranslation("ui");
  if (!isKnowledgeRuleCitation(citation)) return null;

  const statement = citation.statement?.trim();
  const quote = citation.quote?.trim();
  const sourceName = citation.sourceName?.trim();
  // Prefer the plain-English rule statement as the chip label — the quote is a
  // raw source-cell fragment ("AT THE EXCEPTION OF BLUEBERRY CORE FF"). Fall
  // back to the quote, then a generic label.
  const label = statement || quote || t("commentCitationLinks.knowledgeRule");

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <button
          type="button"
          data-testid="comment-panel-knowledge-rule-citation"
          aria-label={t("commentCitationLinks.knowledgeRuleCitation")}
          className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-puntt-accent-11 underline underline-offset-2 hover:bg-transparent hover:text-puntt-accent-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
        >
          <Text as="span" size="1" weight="medium">
            {label}
          </Text>
          {sourceName ? (
            <Text
              as="span"
              size="1"
              className="text-puntt-neutral-gray-11"
              data-testid="knowledge-rule-citation-source"
            >
              {`· ${sourceName}`}
            </Text>
          ) : null}
          <ArrowSquareOut size={12} aria-hidden="true" />
        </button>
      </Dialog.Trigger>
      <Dialog.Content
        data-testid="comment-knowledge-rule-citation-dialog"
        className="flex max-h-[min(70vh,420px)] flex-col gap-4 overflow-y-auto"
      >
        <Dialog.Title>
          {t("commentCitationLinks.knowledgeRuleSource")}
        </Dialog.Title>
        <Dialog.Description className="hidden">
          {t("commentCitationLinks.knowledgeRuleExcerpt")}
        </Dialog.Description>
        {statement ? (
          <Text size="2" weight="medium" data-testid="knowledge-rule-statement">
            {statement}
          </Text>
        ) : null}
        {sourceName ? (
          <Text
            size="2"
            className="text-puntt-neutral-gray-11"
            data-testid="knowledge-rule-citation-source-doc"
          >
            {sourceName}
          </Text>
        ) : null}
        {quote ? (
          <>
            <Text size="2" className="text-puntt-neutral-gray-11">
              <em>{t("commentCitationLinks.fromKnowledgeDatabase")}</em>
            </Text>
            <Preview text={quote} />
          </>
        ) : null}
        <Flex justify="end">
          <Button variant="outline" size="2" asChild>
            <Link
              data-testid="comment-knowledge-rule-citation-view-link"
              to={knowledgeDatabaseRoute.to}
              search={{
                ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
                tab: "rules",
                ruleId: citation.ruleId,
                ...(citation.sourceId ? { sourceId: citation.sourceId } : {}),
              }}
              target="_blank"
              rel="noreferrer"
              className="puntt-link flex gap-1"
            >
              <Text>
                {t("commentCitationLinks.viewRuleInKnowledgeDatabase")}
              </Text>
              <ArrowSquareOut width={16} height={16} />
            </Link>
          </Button>
        </Flex>
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            {t("commentCitationLinks.close")}
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function CommentCitationLinks({
  citations,
  onCitationClick,
  vertical = false,
}: {
  citations: TicketMessageCitation[];
  onCitationClick?: (citation: CitationTarget) => void;
  /** Stack each citation on its own line instead of wrapping inline. */
  vertical?: boolean;
}) {
  const { t } = useTranslation("ui");
  if (citations.length === 0) return null;

  return (
    <Flex
      gap="2"
      className="mt-2 shrink-0"
      {...(vertical
        ? { direction: "column" as const, align: "start" as const }
        : { wrap: "wrap" as const })}
    >
      {citations.map((citation, idx) => {
        if (isNavigableFileCitation(citation)) {
          return (
            <NavigableFileCitationLink
              key={`citation-${idx}`}
              citation={citation}
              index={idx}
              onCitationClick={onCitationClick}
            />
          );
        }
        if (isTextCitation(citation)) {
          return (
            <TextCitationLink key={`citation-${idx}`} citation={citation} />
          );
        }
        if (isKnowledgeDbCitation(citation)) {
          return (
            <KnowledgeDbCitationLink
              key={`citation-${idx}`}
              citation={citation}
            />
          );
        }
        if (isKnowledgeRuleCitation(citation)) {
          return (
            <KnowledgeRuleCitationLink
              key={`citation-${idx}`}
              citation={citation}
            />
          );
        }
        return (
          <Flex
            key={`citation-${idx}`}
            gap="1"
            align="center"
            className="text-puntt-neutral-gray-9"
          >
            <Text size="1">{t("commentCitationLinks.source")}</Text>
            <Warning size={12} aria-hidden="true" />
          </Flex>
        );
      })}
    </Flex>
  );
}
